import React, { Suspense, lazy, useContext, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { GlobalContext } from './context/GlobalContext';
import AppShell from './shell/AppShell';
import StubPage from './shell/StubPage';

// MainApp: authed users land here. Every top-level route is registered —
// migrated screens lazy-load their real component, unmigrated ones render
// <StubPage> so nav, auth, and layout are already the final look.

const Dashboard      = lazy(() => import('./pages/Dashboard'));
const Messenger      = lazy(() => import('./pages/Messenger'));
const Customers      = lazy(() => import('./pages/Customers'));
const BDM            = lazy(() => import('./pages/BDM'));
const BDMDashboard   = lazy(() => import('./pages/bdm/BDMDashboard'));
const BDMVisitSearch = lazy(() => import('./pages/bdm/VisitSearch'));
const BDMVisitLog    = lazy(() => import('./pages/bdm/VisitLog'));
const BDMFollowUps   = lazy(() => import('./pages/bdm/FollowUps'));
const BDMVisitRequests = lazy(() => import('./pages/bdm/VisitRequests'));
const BDMFeedback    = lazy(() => import('./pages/bdm/FeedbackTracker'));
const BDMFeedbackDetail = lazy(() => import('./pages/bdm/FeedbackDetail'));
const BDMOversight   = lazy(() => import('./pages/bdm/Oversight'));
const BDMAdmin       = lazy(() => import('./pages/bdm/BDMAdmin'));
const BDMMyActivity  = lazy(() => import('./pages/bdm/MyBDMActivity'));
const CallAnalysis   = lazy(() => import('./pages/CallAnalysis'));
const ReportsHub     = lazy(() => import('./pages/Reports'));
const ReportBranch   = lazy(() => import('./pages/reports/BranchPerformance'));
const ReportSales    = lazy(() => import('./pages/reports/SalesAnalysis'));
const ReportCustAna  = lazy(() => import('./pages/reports/CustomerAnalysis'));
const ReportTopCust  = lazy(() => import('./pages/reports/TopCustomers'));
const ReportOpDaily  = lazy(() => import('./pages/reports/OperatorDaily'));
const ReportGMRep    = lazy(() => import('./pages/reports/GrossMarginByRep'));
const ReportSixMoGrp = lazy(() => import('./pages/reports/SixMonthByGroup'));
const ReportSixMoAr  = lazy(() => import('./pages/reports/SixMonthByArea'));
const MyVCP          = lazy(() => import('./pages/MyVCP'));
const VCPManagement  = lazy(() => import('./pages/VCPManagement'));
const MyHolidays     = lazy(() => import('./pages/MyHolidays'));
const WorkshopEssen  = lazy(() => import('./pages/WorkshopEssen'));
// Fleet management (revamp) — self-contained module under src/pages/fleet.
const FleetHome           = lazy(() => import('./pages/fleet/FleetHome'));
const FleetVehicles       = lazy(() => import('./pages/fleet/FleetVehicles'));
const FleetVehicleDetail  = lazy(() => import('./pages/fleet/FleetVehicleDetail'));
const FleetWorkItems      = lazy(() => import('./pages/fleet/FleetWorkItems'));
const FleetRepairs        = lazy(() => import('./pages/fleet/FleetRepairs'));
const FleetRepairDetail   = lazy(() => import('./pages/fleet/FleetRepairDetail'));
const FleetPenalties      = lazy(() => import('./pages/fleet/FleetPenalties'));
const FleetPenaltyDetail  = lazy(() => import('./pages/fleet/FleetPenaltyDetail'));
const FleetDrivers        = lazy(() => import('./pages/fleet/FleetDrivers'));
const FleetDriverDetail   = lazy(() => import('./pages/fleet/FleetDriverDetail'));
const TmaCalendar    = lazy(() => import('./pages/tma/Calendar'));
const TmaShifts      = lazy(() => import('./pages/tma/Shifts'));
const TmaHolidays    = lazy(() => import('./pages/tma/Holidays'));
const SettingsStaff      = lazy(() => import('./pages/settings/Staff'));
const SettingsBranch     = lazy(() => import('./pages/settings/Branch'));
const SettingsAssignCust = lazy(() => import('./pages/settings/AssignCust'));
const SettingsMessages   = lazy(() => import('./pages/settings/Messages'));
const SettingsShifts     = lazy(() => import('./pages/settings/Shifts'));
const SettingsHolidays   = lazy(() => import('./pages/settings/Holidays'));
const SettingsAbout         = lazy(() => import('./pages/settings/About'));
const SettingsWhatsNew      = lazy(() => import('./pages/settings/WhatsNew'));
const SettingsNotifications = lazy(() => import('./pages/settings/Notifications'));
const SettingsWallboards    = lazy(() => import('./pages/settings/Wallboards'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const Logins         = lazy(() => import('./pages/admin/Logins'));
const Notifications  = lazy(() => import('./pages/Notifications'));
const ChatRoute      = lazy(() => import('./pages/chat/ChatRoute'));
const DriftRules     = lazy(() => import('./pages/control-panel/CustomerDrift'));
const DriftAlerts    = lazy(() => import('./pages/control-panel/DriftAlerts'));
const DriftReport    = lazy(() => import('./pages/control-panel/DriftReport'));
const DriftCustomer  = lazy(() => import('./pages/control-panel/DriftCustomer'));
const VisitPlans     = lazy(() => import('./pages/control-panel/VisitPlans'));
const VisitPlanEditor = lazy(() => import('./pages/control-panel/VisitPlanEditor'));

const Fallback = () => (
  <div style={{ padding: 32, color: 'var(--ink-3)' }} className="label">Loading…</div>
);

// Route-level wrapper that picks the right named export from the bundled
// "SimpleReports" module so we only pay one lazy-chunk for 4 light reports.
const SimpleReportsRoute = ({ which }) => {
  const [Comp, setComp] = React.useState(null);
  React.useEffect(() => {
    let cancel = false;
    import('./pages/reports/SimpleReports').then(mod => {
      if (cancel) return;
      setComp(() => ({
        area:     mod.AreaComparison,
        advisor:  mod.AdvisorPerformance,
        bdm:      mod.BDMActivity,
        contacts: mod.CustomerContacts,
      }[which] || null));
    });
    return () => { cancel = true; };
  }, [which]);
  return Comp ? <Comp/> : <Fallback/>;
};

// Hard redirect for fleet users: any non-fleet / non-holidays / non-TMA page
// bounces them to their vehicle dashboard (spec §3).
const FleetGuard = ({ children }) => {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { uinfo } = useContext(GlobalContext);
  useEffect(() => {
    if (uinfo?.Status !== 'fleet') return;
    const allowed = pathname.startsWith('/fleet')
      || pathname.startsWith('/myholidays')
      || pathname.startsWith('/tma')
      || pathname.startsWith('/notifications');
    if (!allowed) nav('/fleet/vehicles', { replace: true });
  }, [pathname, uinfo, nav]);
  return children;
};

// Route-level role guard (spec §3 table). Wraps elements that have
// stricter access than the default "anyone authed" rule.
const RoleGate = ({ allow, children }) => {
  const { uinfo } = useContext(GlobalContext);
  if (!uinfo) return null;
  if (!allow.includes(uinfo.Status)) {
    return (
      <StubPage
        section="403"
        title="Not for you."
        description="Your role doesn't have access to this section."
      />
    );
  }
  return children;
};

const MainApp = () => (
  <AppShell>
    <FleetGuard>
      <Suspense fallback={<Fallback/>}>
        <Routes>
          {/* Dashboard */}
          <Route path="/" element={<Dashboard/>} />

          {/* Messenger */}
          <Route path="/messenger" element={<Messenger/>} />

          {/* Reports */}
          <Route path="/reports"          element={<ReportsHub/>} />
          <Route path="/reports/branch"   element={<ReportBranch/>} />
          <Route path="/reports/customer" element={<ReportCustAna/>} />
          <Route path="/reports/sales"    element={<ReportSales/>} />
          <Route path="/reports/top-customers"       element={<ReportTopCust/>} />
          <Route path="/reports/gross-margin-by-rep" element={<ReportGMRep/>} />
          <Route path="/reports/six-month-by-group"  element={<ReportSixMoGrp/>} />
          <Route path="/reports/six-month-by-area"   element={<ReportSixMoAr/>} />
          <Route path="/reports/area"     element={<SimpleReportsRoute which="area"/>} />
          <Route path="/reports/advisor"  element={<SimpleReportsRoute which="advisor"/>} />
          <Route path="/reports/operator-daily" element={<ReportOpDaily/>} />
          <Route path="/reports/bdm"      element={<SimpleReportsRoute which="bdm"/>} />
          <Route path="/reports/contacts" element={<SimpleReportsRoute which="contacts"/>} />

          {/* My VCP */}
          <Route path="/my-vcp"            element={<MyVCP/>} />
          <Route path="/my-vcp/management" element={<VCPManagement/>} />

          {/* Customers */}
          <Route path="/customers" element={<Customers/>} />

          {/* BDM Manager — admin / bm / rep only (spec §3). Oversight is mgmt-only. */}
          <Route path="/bdm"            element={<RoleGate allow={['admin','rep']}><BDMDashboard/></RoleGate>} />
          <Route path="/bdm/visits"        element={<RoleGate allow={['admin','rep']}><BDM/></RoleGate>} />
          <Route path="/bdm/visit-search"  element={<RoleGate allow={['admin','rep']}><BDMVisitSearch/></RoleGate>} />
          <Route path="/bdm/logs"          element={<RoleGate allow={['admin','rep']}><BDMVisitLog/></RoleGate>} />
          <Route path="/bdm/follow-ups" element={<RoleGate allow={['admin','rep']}><BDMFollowUps/></RoleGate>} />
          <Route path="/bdm/follow-ups/:id" element={<RoleGate allow={['admin','rep']}><BDMFollowUps/></RoleGate>} />
          <Route path="/bdm/visit-requests" element={<RoleGate allow={['admin','rep']}><BDMVisitRequests/></RoleGate>} />
          <Route path="/bdm/visit-requests/:id" element={<RoleGate allow={['admin','rep']}><BDMVisitRequests/></RoleGate>} />
          <Route path="/bdm/feedback"   element={<RoleGate allow={['admin','rep']}><BDMFeedback/></RoleGate>} />
          <Route path="/bdm/feedback/:id" element={<RoleGate allow={['admin','rep']}><BDMFeedbackDetail/></RoleGate>} />
          <Route path="/bdm/oversight"  element={<RoleGate allow={['admin']}><BDMOversight/></RoleGate>} />
          <Route path="/bdm/admin"      element={<RoleGate allow={['admin']}><BDMAdmin/></RoleGate>} />
          {/* Sales operators' BDM comms landing (B-030). Admin allowed for support. */}
          <Route path="/bdm/my-activity" element={<RoleGate allow={['admin','sales']}><BDMMyActivity/></RoleGate>} />

          {/* Call Analysis (admin/bm) — overview / customers / staff / extensions */}
          <Route path="/calls"            element={<RoleGate allow={['admin','bm']}><CallAnalysis/></RoleGate>} />
          <Route path="/calls/customers"  element={<RoleGate allow={['admin','bm']}><CallAnalysis/></RoleGate>} />
          <Route path="/calls/staff"      element={<RoleGate allow={['admin','bm']}><CallAnalysis/></RoleGate>} />
          <Route path="/calls/extensions" element={<RoleGate allow={['admin','bm']}><CallAnalysis/></RoleGate>} />

          {/* My Holidays */}
          <Route path="/myholidays" element={<MyHolidays/>} />

          {/* TMA */}
          <Route path="/tma"          element={<TmaCalendar/>} />
          <Route path="/tma/calendar" element={<TmaCalendar/>} />
          <Route path="/tma/holidays" element={<TmaHolidays/>} />
          <Route path="/tma/shifts"   element={<TmaShifts/>} />

          {/* Fleet management (admin / bm / fleet). Deep-link targets for the
              in-app notification inbox: /fleet/vehicles/:id, /fleet/repairs/:id,
              /fleet/penalties/:id. */}
          <Route path="/fleet"              element={<FleetHome/>} />
          <Route path="/fleet/vehicles"     element={<FleetVehicles/>} />
          <Route path="/fleet/vehicles/:id" element={<FleetVehicleDetail/>} />
          <Route path="/fleet/work-items"   element={<FleetWorkItems/>} />
          <Route path="/fleet/repairs"      element={<FleetRepairs/>} />
          <Route path="/fleet/repairs/:id"  element={<FleetRepairDetail/>} />
          <Route path="/fleet/penalties"    element={<FleetPenalties/>} />
          <Route path="/fleet/penalties/:id" element={<FleetPenaltyDetail/>} />
          <Route path="/fleet/drivers"      element={<FleetDrivers/>} />
          <Route path="/fleet/drivers/:id"  element={<FleetDriverDetail/>} />

          {/* Workshop Essentials */}
          <Route path="/workshop"      element={<WorkshopEssen/>} />
          <Route path="/workshopessen" element={<WorkshopEssen/>} />

          {/* Settings */}
          <Route path="/settings"            element={<SettingsAbout/>} />
          <Route path="/settings/staff"      element={<SettingsStaff/>} />
          <Route path="/settings/branch"     element={<SettingsBranch/>} />
          <Route path="/settings/assigncust" element={<SettingsAssignCust/>} />
          <Route path="/settings/messages"   element={<SettingsMessages/>} />
          <Route path="/settings/shifts"     element={<SettingsShifts/>} />
          <Route path="/settings/holidays"      element={<SettingsHolidays/>} />
          <Route path="/settings/notifications" element={<SettingsNotifications/>} />
          <Route path="/settings/wallboards"    element={<SettingsWallboards/>} />
          <Route path="/settings/about"         element={<SettingsAbout/>} />
          <Route path="/settings/whats-new"     element={<SettingsWhatsNew/>} />

          {/* User Management (admin) */}
          <Route path="/users" element={<RoleGate allow={['admin']}><UserManagement/></RoleGate>} />
          <Route path="/users/logins" element={<RoleGate allow={['admin']}><Logins/></RoleGate>} />

          {/* Control Panel · Customer Drift (admin/bm) */}
          <Route path="/control-panel/customer-drift"                   element={<RoleGate allow={['admin','bm']}><DriftRules/></RoleGate>} />
          <Route path="/control-panel/customer-drift/alerts"            element={<RoleGate allow={['admin','bm']}><DriftAlerts/></RoleGate>} />
          <Route path="/control-panel/customer-drift/report"            element={<RoleGate allow={['admin','bm']}><DriftReport/></RoleGate>} />
          <Route path="/control-panel/customer-drift/customer/:account" element={<RoleGate allow={['admin','bm']}><DriftCustomer/></RoleGate>} />

          {/* Control Panel · Visit Plans (admin only) */}
          <Route path="/control-panel/visit-plans"     element={<RoleGate allow={['admin','rep']}><VisitPlans/></RoleGate>} />
          <Route path="/control-panel/visit-plans/:id" element={<RoleGate allow={['admin','rep']}><VisitPlanEditor/></RoleGate>} />

          {/* Notifications */}
          <Route path="/notifications" element={<Notifications/>} />

          {/* Chat deep-link target (notification links route here, then bounce) */}
          <Route path="/chat/:entityType/:entityId" element={<ChatRoute/>} />

          {/* Legacy compatibility — same role gates as /bdm/* */}
          <Route path="/repvisits"            element={<RoleGate allow={['admin','rep']}><BDM/></RoleGate>} />
          <Route path="/repvisits/logs"       element={<RoleGate allow={['admin','rep']}><BDMVisitLog/></RoleGate>} />
          <Route path="/repvisits/follow-ups" element={<RoleGate allow={['admin','rep']}><BDMFollowUps/></RoleGate>} />
          <Route path="/repvisits/oversight"  element={<RoleGate allow={['admin','rep']}><BDMOversight/></RoleGate>} />
          <Route path="/sales"     element={<StubPage section="" title="Sales." description="Legacy route. Superseded by /reports/sales."/>} />
          <Route path="/delivery"  element={<StubPage section="" title="Delivery." description="Legacy route."/>} />

          <Route path="*" element={
            <StubPage section="404" title="Page not found."
              description="The URL you followed doesn't exist in this app."/>
          }/>
        </Routes>
      </Suspense>
    </FleetGuard>
  </AppShell>
);

export default MainApp;
